import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { DocumentType } from '@kmdx/contracts';
import { api, ApiError } from '../client';
import { qk } from '../keys';
import type { DocumentSummary } from '../types';

/**
 * Качване на файл от офиса — заявката от клиента, фактура, талон, документ на
 * шофьор. Същият път като телефона на шофьора: сървърът дава линк с кратък
 * живот, файлът отива по него, после „готово" сверява размера и контролната
 * сума. Без тази сверка повреден PDF минаваше за качен.
 */
export interface UploadTarget {
  type: DocumentType;
  orderId?: string;
  tripId?: string;
  vehicleId?: string;
  driverId?: string;
  notes?: string;
  /** До кога важи документът — ГГГГ-ММ-ДД. */
  validUntil?: string;
}

async function sha256Hex(buffer: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export async function uploadDocument(file: File, target: UploadTarget): Promise<DocumentSummary> {
  if (file.size > 25 * 1024 * 1024) {
    throw new ApiError(422, 'VALIDATION_FAILED', 'Файлът е по-голям от 25 MB.');
  }
  const bytes = await file.arrayBuffer();
  const sha256 = await sha256Hex(bytes);
  const mimeType = file.type || 'application/octet-stream';
  const ticket = await api.post<{ documentId: string; uploadUrl: string | null; duplicate: boolean }>(
    '/documents/init',
    {
      clientDocId: crypto.randomUUID(),
      type: target.type,
      fileName: file.name,
      mimeType,
      sizeBytes: file.size,
      sha256,
      ...(target.orderId ? { orderId: target.orderId } : {}),
      ...(target.tripId ? { tripId: target.tripId } : {}),
      ...(target.vehicleId ? { vehicleId: target.vehicleId } : {}),
      ...(target.driverId ? { driverId: target.driverId } : {}),
      ...(target.notes ? { notes: target.notes } : {}),
      ...(target.validUntil ? { validUntil: target.validUntil } : {}),
    },
  );
  if (ticket.uploadUrl) {
    let put: Response;
    try {
      put = await fetch(ticket.uploadUrl, { method: 'PUT', headers: { 'Content-Type': mimeType }, body: bytes });
    } catch {
      throw ApiError.network();
    }
    if (!put.ok) {
      throw new ApiError(put.status, 'UPLOAD_FAILED', 'Файлът не се качи — опитайте пак.');
    }
  }
  return api.post<DocumentSummary>(`/documents/${ticket.documentId}/complete`, {
    sizeBytes: file.size,
    sha256,
  });
}

export function useUploadDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, target }: { file: File; target: UploadTarget }) => uploadDocument(file, target),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.documents.all });
      void queryClient.invalidateQueries({ queryKey: qk.orders.all });
      void queryClient.invalidateQueries({ queryKey: qk.trips.all });
      void queryClient.invalidateQueries({ queryKey: qk.vehicles.all });
      void queryClient.invalidateQueries({ queryKey: qk.drivers.all });
      void queryClient.invalidateQueries({ queryKey: ['dossier'] });
    },
  });
}
